import { ChargePointLog } from '../../app/ApiGen';
import { Phase, ThreePhase } from '../meterValues';
import { roundTo2DecimalPlaces, sumValues } from '../number';
import { Normalised } from '../request';

type PerPhaseData = Record<ThreePhase, number>;

export const phases: ThreePhase[] = ['L1', 'L2', 'L3'];

export type LLMClient = {
  Name: string;
  MaxCurrent: number;
  CanCharge: boolean;
  AllowedCurrent: number;
  ChargingOnPhase: Phase;
  ChargeNow: boolean;
  MeasuredCurrent: number;
  WantedCurrent: number;
  UpTime: number;
};

export type LLMServer = {
  Mode: 'Server';
  SupplyCurrent?: PerPhaseData;
  GroupLimit?: number | PerPhaseData;
  SharedLimit?: number;
  MaxSafeLimit?: PerPhaseData;
  ClientInfos: {
    MaxClients: number;
    Clients: LLMClient[];
  };
};

export type LLMServerStatusLog = Omit<Normalised<ChargePointLog>, 'eventPayload'> & {
  eventPayload: LLMServer;
};

export type LimitCalculationData = {
  phase: Phase;
  groupLimit: number;
  nonChargingCurrents: number;
  total: number;
  totalAboveLimit: boolean | undefined;
};

export function getGroupLimit(args: { groupLimit: LLMServer['GroupLimit']; phase: ThreePhase }): number {
  const { groupLimit, phase } = args;
  if (groupLimit === undefined) {
    return 0;
  }
  if (typeof groupLimit === 'number') {
    return groupLimit;
  }
  return groupLimit[phase];
}

export const getLimitCalculationData = (args: {
  log: LLMServerStatusLog;
  supplyCurrentLimit: number | undefined;
}): LimitCalculationData[] => {
  const { ClientInfos, GroupLimit, SupplyCurrent } = args.log.eventPayload;
  return phases.map((phase) => {
    const groupLimit = getGroupLimit({ groupLimit: GroupLimit, phase });
    const supplyCurrent = SupplyCurrent ? SupplyCurrent[phase] : 0;
    const measuredCurrents = sumValues(
      ClientInfos.Clients.map((c) =>
        c.ChargingOnPhase === phase || c.ChargingOnPhase === 'All' ? c.MeasuredCurrent : 0,
      ),
    );

    const nonChargingCurrents = supplyCurrent - measuredCurrents;
    const total = groupLimit + nonChargingCurrents;
    return {
      phase,
      groupLimit: roundTo2DecimalPlaces(groupLimit),
      nonChargingCurrents: roundTo2DecimalPlaces(nonChargingCurrents),
      total: roundTo2DecimalPlaces(total),
      totalAboveLimit: args.supplyCurrentLimit ? total > args.supplyCurrentLimit : false,
    };
  });
};
